import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React from 'react';

import useTranslation from '../../hooks/use_translation';
import BadgeContainer from '../framework/badges/badge_container';
import BadgeDiscount from '../framework/badges/badge-discount';
import BtnIcon from '../framework/buttons/btn_icon';
import Clickable from '../framework/clickable';
import GroupLink from '../framework/group_link';
import Included from '../framework/included';
import PriceItem from '../shop/price_item';
import Trans from '../translation/trans';
import MarkerThumb from './marker-thumb';
import ProductBadges from './product-badges';
import ProductShopButton from './product-shop-button';

const ProductItemList = ({
	currency,
	excerpt,
	groups,
	markers,
	onAddToCart,
	page,
	price,
	price_discount,
	product_default,
	product_id,
	product_image,
	product_settings,
	product_variant_id,
	slug,
	state,
	subtitle,
	title,
	tracking,
	type,
}) => {
	const { t } = useTranslation('component_test_product_list_item');
	const id_key = `product-item-${product_variant_id}-${slug}`;

	return (
		<Clickable
			as="div"
			className="product-item-list product-list-tests"
			id={id_key}
		>
			<div className="card-container product-list-item position-relative">
				<BadgeContainer position="top_right">
					<BadgeDiscount
						className=""
						price={price}
						price_discount={price_discount}
					/>
				</BadgeContainer>
				<div className="d-inherit py-3 px-3 px-lg-0 border-bottom">
					<div className="d-flex align-items-start justify-content-between py-3">
						<div className="container-start">
							<div className="contrainer-start-wrapper">
								<div className="d-flex align-items-center">
									<div className="pe-1 ">
										{product_image && (
											<>
												<GatsbyImage
													alt={title}
													className="img-fluid thumb-lg product-list-img"
													draggable="false"
													image={getImage(product_image)}
												/>
											</>
										)}
										{!product_image && (
											<MarkerThumb
												groups={groups}
												markers={markers}
												product_type={type}
												size="lg"
											/>
										)}
									</div>

									<div className="ps-1">
										{subtitle && (
											<div className="list-subtitle text-muted me-1">
												{subtitle}
											</div>
										)}

										<h3 className="text-primary-dark mb-0 display-inline card-title text-break">
											<GroupLink
												className="clickable text-decoration-none fw-600 text-primary-dark"
												groups={
													product_default?.group_id
														? [product_default?.group_id]
														: groups
												}
												page={page}
												product_id={product_id}
											>
												{title}
											</GroupLink>
										</h3>
										<div className="mt-1">
											<ProductBadges
												include_types={['markers', 'self_test']}
												product_id={product_id}
											/>
										</div>
										{markers?.length > 0 && (
											<div className="mt-1">
												<Included
													product_settings={product_settings}
													size="sm"
												/>
											</div>
										)}
									</div>
								</div>
								<div className="list-check card-list text-break white-space-break mt-3 text-muted pe-lg-5 x-small">
									<Trans defaults={excerpt} t={t} />
								</div>
							</div>
						</div>

						<div className="container-end d-flex flex-column align-items-end">
							<div className="price text-center mb-1 mt-lg-3">
								<p className="list-price fw-500 mb-0 text-number">
									<PriceItem
										currency={currency?.code}
										price={price}
										price_discount={price_discount}
									/>
								</p>
							</div>

							{state != 'affiliate' && (
								<ProductShopButton
									onAddToCart={onAddToCart}
									product_id={product_id}
									tracking={tracking}
									variant="slim"
								/>
							)}

							<BtnIcon
								className="mt-1"
								groups={groups}
								linkType="group_link"
								page={page}
								product_id={product_id}
								variant="transparent"
							/>
						</div>
					</div>
				</div>
			</div>
		</Clickable>
	);
};

ProductItemList.propTypes = {
	currency: PropTypes.object,
	excerpt: PropTypes.string,
	group_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	groups: PropTypes.array,
	markers: PropTypes.array,
	onAddToCart: PropTypes.func,
	page: PropTypes.string,
	price: PropTypes.string,
	price_discount: PropTypes.string,
	product_default: PropTypes.object,
	product_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	product_image: PropTypes.object,
	product_settings: PropTypes.object,
	product_variant_id: PropTypes.number,
	slug: PropTypes.string,
	state: PropTypes.string,
	status: PropTypes.string,
	subtitle: PropTypes.string,
	title: PropTypes.string,
	tracking: PropTypes.object,
	type: PropTypes.string,
};

export default ProductItemList;
